import React, { useEffect, useState } from 'react';
import SearchBox from './SearchBox';
import { matchesQuery } from '../utils/search';
import toast from 'react-hot-toast';

import CatalogService from '../services/catalog.service';
import { useTranslation } from 'react-i18next';
import Spinner from './Spinner';
import BilingualField, { emptyBilingual, bilingualFromRow, hasAnyLang, allLangValues } from './BilingualField';

const I18N_FIELDS = ['name', 'description'];
const emptyForm = emptyBilingual(I18N_FIELDS);

const CreateCatalog = () => {
  const { t } = useTranslation();
  const [catalogs, setCatalogs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = catalogs.filter((c) => matchesQuery(query, ...allLangValues(c, 'name'), ...allLangValues(c, 'description')));

  const load = () => {
    setLoading(true);
    CatalogService.getAll()
      .then((res) => setCatalogs(res.data))
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const resolveMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message || t('product.errors.generic');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage('');

    if (!hasAnyLang(form, 'name')) {
      toast.error(t('createCatalog.nameRequired'));
      return;
    }

    const action = editingId ? CatalogService.update(editingId, form) : CatalogService.create(form);

    action
      .then(() => {
        setMessage(editingId ? t('createCatalog.updated') : t('createCatalog.created'));
        resetForm();
        load();
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  const handleEdit = (c) => {
    setEditingId(c.id);
    setForm(bilingualFromRow(c, I18N_FIELDS));
    setMessage('');
  };

  const handleDelete = (id) => {
    if (!window.confirm(t('createCatalog.confirmDelete'))) return;
    setMessage('');
    CatalogService.remove(id)
      .then(() => {
        setMessage(t('createCatalog.deleted'));
        if (editingId === id) resetForm();
        load();
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  return (
    <div className='container mt-4'>
      <h3>{editingId ? t('createCatalog.editTitle') : t('createCatalog.createTitle')}</h3>

      {message && <div className='alert alert-success'>{message}</div>}

      <form onSubmit={handleSubmit} className='card card-body mb-4'>
        <BilingualField field='name' label={t('common.name')} required form={form} onChange={handleChange} maxLength={150} placeholder={t('createCatalog.namePlaceholder')} />
        <BilingualField field='description' label={t('common.description')} multiline form={form} onChange={handleChange} placeholder={t('product.descriptionPlaceholder')} />
        <div className='d-flex gap-2'>
          <button type='submit' className='btn btn-primary'>
            {editingId ? t('common.update') : t('common.create')}
          </button>
          {editingId && (
            <button type='button' className='btn btn-secondary' onClick={resetForm}>
              {t('common.cancel')}
            </button>
          )}
        </div>
      </form>

      <h4>{t('createCatalog.listTitle')}</h4>
      {loading ? (
        <Spinner />
      ) : catalogs.length === 0 ? (
        <p className='text-muted'>{t('createCatalog.empty')}</p>
      ) : (
        <>
        <SearchBox
          className='mb-3'
          value={query}
          onChange={setQuery}
          placeholder={t('createCatalog.searchPlaceholder', 'Search catalogs by name or description...')}
        />
        {filtered.length === 0 ? (
          <p className='text-muted'>{t('common.noResults', 'No results match your search.')}</p>
        ) : (
        <div className='table-responsive rounded'>
          <table className='table table-hover'>
            <thead>
              <tr>
                <th style={{ width: 60 }}>{t('common.id')}</th>
                <th>{t('common.name')}</th>
                <th>{t('common.description')}</th>
                <th style={{ width: 160 }}>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>{c.name}</td>
                  <td>{c.description}</td>
                  <td>
                    <button className='btn btn-sm btn-outline-primary me-2' onClick={() => handleEdit(c)}>
                      {t('common.edit')}
                    </button>
                    <button className='btn btn-sm btn-outline-danger' onClick={() => handleDelete(c.id)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}
    </div>
  );
};

export default CreateCatalog;
